import React, { useState, useRef } from 'react';
import { Modal, Button, message, Spin, Tag, Divider, Row, Col, Card, Image } from 'antd';
import { UploadOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import { usePermissions } from '../../contexts/PermissionContext';
import './ProductDetailModal.scss';

interface ProductDetailModalProps {
  visible: boolean;
  product: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  visible,
  product,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('picture', file);

      const response = await Api.post(`api/admin/product-picture/${product?.id}`, formData);
      return response;
    },
    onSuccess: () => {
      message.success(t('products.imageUploadSuccess', 'Image uploaded successfully'));
      queryClient.invalidateQueries({ queryKey: ['products'] });
      onSuccess?.();
      onClose(); // Close the modal after successful upload
    },
    onError: (error: any) => {
      // Handle different types of errors
      if (error.response?.status === 401) {
        message.error(
          t('products.uploadUnauthorized', 'Authentication failed. Please login again.')
        );
        // Don't redirect to login for upload errors, just show the error
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error(t('products.imageUploadError', 'Failed to upload image'));
      }
    },
  });

  const handleImageUpload = (file: File) => {
    // Check permission before uploading
    if (!hasPermission('update_product')) {
      message.error(
        t('permissions.accessDenied') || 'You do not have permission to update product images'
      );
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      message.error(t('products.uploadUnauthorized', 'Authentication failed. Please login again.'));
      return;
    }

    setUploading(true);
    uploadImageMutation.mutate(file, {
      onSettled: () => {
        setUploading(false);
      },
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error(t('products.invalidImageType', 'Please select a valid image file'));
        return;
      }

      // Validate file size (max 5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error(t('products.imageTooLarge', 'Image must be smaller than 5MB'));
        return;
      }

      handleImageUpload(file);
    }
  };

  const handlePreview = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    setPreviewVisible(true);
  };

  const getDisplayName = (item: any) => {
    const currentLang = localStorage.getItem('i18nextLng') || 'en';
    return currentLang === 'ar' ? item?.name_ar : item?.name_en || item?.name;
  };

  const getDisplayDescription = (item: any) => {
    const currentLang = localStorage.getItem('i18nextLng') || 'en';
    return currentLang === 'ar'
      ? item?.description_ar
      : item?.description_en || item?.description || '';
  };

  const getImageUrl = (picture: string) => {
    return picture ? `https://driveshield.net/storage/${picture}` : '';
  };

  const formatPrice = (price: string | number) => {
    return parseFloat(price?.toString() || '0').toFixed(2);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const currentLang = localStorage.getItem('i18nextLng') || 'en';

    if (currentLang === 'ar') {
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  };

  // Get current language for RTL support
  const currentLang = localStorage.getItem('i18nextLng') || 'en';
  const isRTL = currentLang === 'ar';

  return (
    <>
      <Modal
        title={
          <div className="modal-header">
            <span>{t('products.productDetails', 'Product Details')}</span>
            <span className="product-name">{getDisplayName(product)}</span>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        className="product-detail-modal"
        destroyOnClose
      >
        <Spin spinning={uploading}>
          <div className="product-detail-content" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Product Images Section */}
            <Card title={t('products.productImages', 'Product Images')} className="images-section">
              <Row gutter={[16, 16]}>
                <Col span={hasPermission('update_product') ? 12 : 24}>
                  <div className="image-upload-area">
                    <div className="current-image">
                      {product?.picture ? (
                        <div className="image-container">
                          <Image
                            src={getImageUrl(product.picture)}
                            alt="Product"
                            width="100%"
                            height={200}
                            style={{ objectFit: 'cover', borderRadius: 8 }}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <div className="image-actions">
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={() => handlePreview(getImageUrl(product.picture))}
                              title={t('common.view', 'View')}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="no-image">
                          <div className="no-image-placeholder">
                            <UploadOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                            <p>{t('products.noImage', 'No Image')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
                {hasPermission('update_product') && (
                  <Col span={12}>
                    <div className="upload-section">
                      <h4>{t('products.uploadNewImage', 'Upload New Image')}</h4>
                      <p className="upload-hint">
                        {t(
                          'products.uploadHint',
                          'Click the button below to upload a new image. Supported formats: JPG, PNG, GIF. Max size: 5MB.'
                        )}
                      </p>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => fileInputRef.current?.click()}
                        loading={uploading}
                        style={{ width: '100%', height: 100 }}
                      >
                        {t('products.selectImage', 'Select Image')}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </Card>

            <Divider />

            {/* Product Information Section */}
            <Card
              title={t('products.productInformation', 'Product Information')}
              className="info-section"
            >
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.name', 'Name')}</label>
                    <div className="value">{getDisplayName(product)}</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.sku', 'SKU')}</label>
                    <div className="value">{product?.sku || '-'}</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.barcode', 'Barcode')}</label>
                    <div className="value">{product?.barcode || '-'}</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.category', 'Category')}</label>
                    <div className="value">
                      <Tag color="blue">
                        {product?.category?.name || t('products.noCategory', 'No Category')}
                      </Tag>
                    </div>
                  </div>
                </Col>
                {/* <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.buyingPrice', 'Buying Price')}</label>
                    <div className="value price-buying">
                      {formatPrice(product?.buying_price)} {t('common.SAR', 'SAR')}
                    </div>
                  </div>
                </Col> */}
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.sellingPrice', 'Selling Price')}</label>
                    <div className="value price-selling">
                      {formatPrice(product?.selling_price)} {t('common.SAR', 'SAR')}
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.columns.status', 'Status')}</label>
                    <div className="value">
                      <Tag className={product?.pos_product ? 'status-active' : 'status-inactive'}>
                        {product?.pos_product
                          ? t('products.status.active', 'Active')
                          : t('products.status.inactive', 'Inactive')}
                      </Tag>
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="info-item">
                    <label>{t('products.columns.description', 'Description')}</label>
                    <div className="value description">
                      {getDisplayDescription(product) ||
                        t('products.noDescription', 'No description available')}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Additional Details Section */}
            <Card
              title={t('products.additionalDetails', 'Additional Details')}
              className="details-section"
            >
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.createdAt', 'Created At')}</label>
                    <div className="value">{formatDate(product?.created_at)}</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{t('products.updatedAt', 'Updated At')}</label>
                    <div className="value">{formatDate(product?.updated_at)}</div>
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        </Spin>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={previewVisible}
        title={t('products.imagePreview', 'Image Preview')}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <div dir={isRTL ? 'rtl' : 'ltr'}>
          <img alt="Preview" style={{ width: '100%' }} src={previewImage} />
        </div>
      </Modal>
    </>
  );
};

export default ProductDetailModal;
